import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import Card from "../../components/Card";
import { useToast } from "../../components/Toast/toastHook";
import {
  EmptyState,
  ErrorBanner,
  ErrorState,
  skeletonTableProps,
} from "../../components/states";
import Modal from "../../components/modal/Modal";
import ServiceForm from "../../components/directory/ServiceForm";
import ServiceDeleteView from "../../components/directory/ServiceDeleteView";
import { formatDate } from "../../utils/dateFormat";
import { GET_ALL_SERVICES } from "../../graphql/serviceQueries";
import { Button, Table } from "antd";
import {
  PlusOutlined as PlusIcon,
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
} from "@ant-design/icons";
import useDebounce from "../../hooks/useDebounce";

const Services = () => {
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalCount: 0,
  });

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formType, setFormType] = useState("add");
  const [selectedService, setSelectedService] = useState(null);
  // For edit
  const [service, setService] = useState(null);

  const [serviceListing, setServiceListing] = useState([]);

  const [searchText, setSearchText] = useState("");
  const debounceValue = useDebounce(searchText, 500);

  const toast = useToast(4000);

  const { data, loading, error, refetch } = useQuery(GET_ALL_SERVICES, {
    fetchPolicy: "no-cache",
    variables: {
      pagination: {
        page: pagination.page,
        limit: pagination.limit,
      },
      searchText: debounceValue,
    },
  });

  const columns = [
    {
      title: "Types",
      dataIndex: "title",
      key: "title",
      render: (text, record) => (
        <Link to={`/administrator/services/${record._id}`}>
          <div className="flex items-center space-x-2 text-black">
            {record.icon && (
              <img
                src={record.icon}
                alt=""
                className="h-[30px] w-[30px] object-cover"
              />
            )}
            <span>{record.title}</span>
          </div>
        </Link>
      ),
    },
    {
      title: "Date Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => (
        <span className="font-medium text-black">{formatDate(createdAt)}</span>
      ),
    },
    {
      title: "Date Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (updatedAt) => (
        <span className="font-medium text-black">{formatDate(updatedAt)}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex items-center space-x-2">
          <Link
            to={`/administrator/services/${record._id}?name=${record.title}`}
          >
            <Button
              className="border-secondary text-secondary"
              icon={<PlusIcon />}
            />
          </Link>
          <Button
            className="border-primary text-primary"
            icon={<EditIcon />}
            onClick={() => handleEdit(record)}
          />

          <Button
            danger
            icon={<DeleteIcon />}
            onClick={() => handleDeleteModalRender(record._id)}
          />
        </div>
      ),
    },
  ];

  const onChange = (pagination) => {
    setPagination((prev) => ({
      ...prev,
      page: pagination?.current,
      limit: pagination?.pageSize,
    }));
  };

  useEffect(() => {
    if (data?.getAllServices?.services)
      setServiceListing(data?.getAllServices?.services);
    setPagination((prev) => ({
      ...prev,
      totalCount: data?.getAllServices?.totalCount,
    }));
  }, [data]);

  useEffect(() => {
    if (!error) return;
    toast("error", error.message);
  });

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleEdit = (directoryToEdit) => {
    setService(directoryToEdit);
    setFormType("edit");
    setShowModal(true);
  };
  const handleModalRender = () => {
    setFormType("add");
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const handleDeleteModalRender = (serviceId) => {
    setSelectedService(serviceId);
    setShowDeleteModal(true);
  };

  const firstLoad = loading && !serviceListing.length;

  return (
    <div id="companies">
      <Breadcrumb pageName="Services" />
      <div className="pb-6">
        <Card>
          <div className="m-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <span className="font-bold text-lg">
                  All Services ({pagination.totalCount || 0})
                </span>
                <div className="relative">
                  <div className="absolute top-1/2 left-1 -translate-y-1/2">
                    <svg
                      className="fill-gray-400"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M9.16666 3.33332C5.945 3.33332 3.33332 5.945 3.33332 9.16666C3.33332 12.3883 5.945 15 9.16666 15C12.3883 15 15 12.3883 15 9.16666C15 5.945 12.3883 3.33332 9.16666 3.33332ZM1.66666 9.16666C1.66666 5.02452 5.02452 1.66666 9.16666 1.66666C13.3088 1.66666 16.6667 5.02452 16.6667 9.16666C16.6667 13.3088 13.3088 16.6667 9.16666 16.6667C5.02452 16.6667 1.66666 13.3088 1.66666 9.16666Z"
                        fill=""
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M13.2857 13.2857C13.6112 12.9603 14.1388 12.9603 14.4642 13.2857L18.0892 16.9107C18.4147 17.2362 18.4147 17.7638 18.0892 18.0892C17.7638 18.4147 17.2362 18.4147 16.9107 18.0892L13.2857 14.4642C12.9603 14.1388 12.9603 13.6112 13.2857 13.2857Z"
                        fill=""
                      />
                    </svg>
                  </div>

                  <input
                    type="text"
                    placeholder="Type to search..."
                    className="w-full rounded-md border border-stroke bg-transparent py-3 pl-6 pr-10 outline-none focus:border-primary focus-visible:shadow-none "
                    onChange={handleSearch}
                  />
                </div>
              </div>
              <button
                className="bg-[#B4C540] h-[36px] px-5 text-center rounded-xl text-[#FFFFFF]"
                onClick={handleModalRender}
              >
                + Add service
              </button>
            </div>
          </div>
          {error && !serviceListing.length ? (
            <ErrorState onRetry={() => refetch()} />
          ) : (
            <>
              {error && <ErrorBanner onRetry={() => refetch()} />}
              <Table
                bordered
                rowKey="_id"
                onChange={onChange}
                {...(firstLoad
                  ? skeletonTableProps(columns)
                  : {
                      columns,
                      dataSource: serviceListing,
                      pagination: {
                        position: ["bottomCenter"],
                        current: pagination.page,
                        pageSize: pagination.limit,
                        total: pagination.totalCount,
                      },
                    })}
                locale={{
                  emptyText: searchText ? (
                    <EmptyState
                      title="No services match your search"
                      description={`Nothing found for "${searchText}".`}
                      actionLabel="Clear search"
                      onAction={() => setSearchText("")}
                    />
                  ) : (
                    <EmptyState
                      title="No services yet"
                      description="Services you add will be offered to companies."
                    />
                  ),
                }}
              />
            </>
          )}
        </Card>
      </div>
      <Modal
        title={formType === "add" ? "Add Service" : "Edit Service"}
        showModal={showModal}
        setShowModal={(value) => {
          setShowModal(value);
          // if (!value) setOffice({});
        }}
      >
        <ServiceForm
          service={service}
          formType={formType}
          handleClose={handleClose}
          onServiceAdded={() => refetch()}
        />
      </Modal>
      <Modal
        title={"Delete Service"}
        showModal={showDeleteModal}
        setShowModal={(value) => {
          setShowDeleteModal(value);
        }}
      >
        <ServiceDeleteView
          setShowDeleteModal={setShowDeleteModal}
          serviceId={selectedService}
          handleClose={() => {
            setShowDeleteModal(false);
            refetch();
          }}
        />
      </Modal>
      {/* <Modal
          title="Assigned Agents"
          showModal={agentsModal}
          setShowModal={(value) => {
            setAgentsModal(value);
            // setAgents([]);
          }}
        ></Modal> */}
    </div>
  );
};

export default Services;
